import gql from 'graphql-tag';
import type { DocumentNode } from 'graphql';
import {
	type GQLExtensionContextExtensionsByTypeVariables,
	type GQLExtensionContextsVariables,
	type GQLExtensionContextsFilter,
	GQLExtensionContextsFilterType,
} from '../graphql/types';
import type { ApolloClient } from 'apollo-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { graphql, fetchQuery, useRelayEnvironment } from 'react-relay';
import { createAtlAttributionHeader } from '../../utils';
import type { useEmitPublicEvent_getExtensionListQuery } from './__generated__/useEmitPublicEvent_getExtensionListQuery.graphql';
import { useRef } from 'react';

export const extensionByTypeQuery: DocumentNode = gql`
	query forge_ui_extensionList($contextIds: [ID!]!, $filter: [ExtensionContextsFilter!]!) {
		extensionContexts(contextIds: $contextIds) {
			extensions(filter: $filter) {
				appId
				properties
				key
			}
		}
	}
`;

type Extension = {
	appId?: string;
	key?: string;
	properties?: any;
};

interface QueryData {
	extensionContexts: {
		extensions: Extension[];
	}[];
}

interface QueryVariables {
	contextIds: GQLExtensionContextsVariables['contextIds'];
	filter?: GQLExtensionContextsFilter[];
	type?: GQLExtensionContextExtensionsByTypeVariables['type'];
}

const CORE_EVENT = 'core:event';
const CLIENT_EVENTS = 'client-events';
const CLIENT_EVENT = 'client-event';

const EVENT_PREFIX = 'forge.bridge.';

type ExtensionDetails = {
	appId: string | undefined;
	id: string;
	installationId?: string;
};

export type EmitPublicEvent = (data: { event: string; payload?: any }) => Promise<void>;

const filterAllowedRecipients = (allExtensions: Extension[], extension: ExtensionDetails) => {
	const matchingExtension = allExtensions.find((ext: Extension) => {
		if (ext && ext?.appId && ext?.key) {
			return (
				ext.appId === extension.appId && (ext.key === CLIENT_EVENTS || ext.key === CLIENT_EVENT)
			);
		}
	});
	const allowedRecipients = matchingExtension?.properties?.allowedRecipients ?? [];

	const filteredAllowedRecipients = allowedRecipients
		.filter((recipient: string) => recipient !== '*')
		.map((recipient: string) => recipient.split('/').pop());

	if (!filteredAllowedRecipients.includes(extension.appId)) {
		filteredAllowedRecipients.push(extension.appId);
	}

	return filteredAllowedRecipients;
};

export const useEmitPublicEvent = (
	contextId: string,
	extension: ExtensionDetails,
	client?: ApolloClient<any>,
): EmitPublicEvent => {
	const relayEnvironment = useRelayEnvironment();
	const allowedRecipientsRef = useRef<string[] | null>(null);

	if (!extension?.appId || !fg('forge-ui-add-public-event-methods')) {
		return async () => {};
	}

	const getAllowedRecipients = async () => {
		if (allowedRecipientsRef.current !== null) {
			allowedRecipientsRef.current;
		}

		if (!client) {
			return [extension.appId];
		}

		try {
			if (relayEnvironment) {
				if (fg('forge-ui-add-ext-list-emit-public-event-query')) {
					const observable = fetchQuery<useEmitPublicEvent_getExtensionListQuery>(
						relayEnvironment,
						graphql`
							query useEmitPublicEvent_getExtensionListQuery(
								$contextIds: [ID!]!
								$filter: [ExtensionContextsFilter!]!
							) {
								extensionContexts(contextIds: $contextIds) {
									extensions(filter: $filter) {
										appId
										properties
										key
									}
								}
							}
						`,
						{
							contextIds: [contextId],
							filter: [
								{
									type: GQLExtensionContextsFilterType.ExtensionType,
									value: [CORE_EVENT],
								},
							],
						},
						{
							networkCacheConfig: {
								metadata: {
									headers: {
										'atl-attribution': createAtlAttributionHeader(),
									},
								},
							},
						},
					);

					return new Promise<string[]>((resolve) => {
						observable.subscribe({
							next: (result) => {
								const allExtensions = result?.extensionContexts?.[0]?.extensions || [];
								const filteredAllowedRecipients = filterAllowedRecipients(
									Array.from(allExtensions),
									extension,
								);
								resolve(filteredAllowedRecipients);
							},
							error: () => {
								resolve([extension.appId ?? '']);
							},
						});
					});
				}
			}

			const response = await client.query<QueryData, QueryVariables>({
				query: extensionByTypeQuery,
				variables: {
					contextIds: [contextId],
					filter: [
						{
							type: GQLExtensionContextsFilterType.ExtensionType,
							value: CORE_EVENT,
						},
					],
				},
			});

			const allExtensions = response.data?.extensionContexts?.[0]?.extensions || [];

			const filteredAllowedRecipients = filterAllowedRecipients(
				Array.from(allExtensions),
				extension,
			);

			return filteredAllowedRecipients;
		} catch {
			return [extension.appId];
		}
	};

	return async (eventData: { event: string; payload?: any }) => {
		const { event, payload } = eventData;

		if (allowedRecipientsRef.current === null) {
			const allowedRecipients = await getAllowedRecipients();
			allowedRecipientsRef.current = allowedRecipients;
		}

		const newEvent = new CustomEvent(`${EVENT_PREFIX}${event}`, {
			detail: {
				extension,
				payload,
				allowedRecipients: allowedRecipientsRef.current ?? [extension.appId],
			},
		});

		window.document.dispatchEvent(newEvent);
	};
};
